// SOK-34 (INF-07): los errores del tótem llegan a Sentry, proyecto self-ordering-kiosk de la organización
// motion-displays-x3, junto con el resto de las apps de la plataforma.
//
// - El DSN viene de VITE_SENTRY_DSN al compilar (secreto de GitHub Actions, o src/.env.{mode}.local a mano), nunca
//   del código. Sin él Sentry queda apagado y el tótem funciona igual. En `vite dev` está apagado salvo
//   VITE_SENTRY_IN_DEV=true; esos eventos llevan el tag dev=true.
// - Tags de la plataforma (INF-07): app, env (también como environment), client_id, instance_id y release
//   (self-ordering-kiosk@<versión de package.json>); más cart_id. client_id, instance_id y cart_id se leen de la
//   configuración del tótem al enviar cada evento, porque cambian sin recargar (asistente, nuevo carro).
// - Nada personal ni secreto: sendDefaultPii apagado, sin usuario, sin breadcrumbs de consola (el código loguea
//   carros y respuestas del POS) y todo evento pasa por redactForSentry.
import * as Sentry from "@sentry/react";
import appData from "../../package.json";
import { redactForSentry } from "./sentryRedaction.js";

export const APP = "self-ordering-kiosk";
export const RELEASE = `${APP}@${appData.version}`;

const TAG_VALUE = /^[\w.:@-]{1,128}$/;

const readStorage = (storage, key) => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

// Solo ids cortos llegan a ser tags.
const safeTags = (tags) => Object.fromEntries(Object.entries(tags)
  .filter(([, value]) => (typeof value === "string" || typeof value === "number") && TAG_VALUE.test(String(value)))
  .map(([name, value]) => [name, String(value)]));

/**
 * Tags del tótem según su configuración guardada (ver saveConfig en App.jsx) y el carro en curso.
 * @param {Storage} [storage]
 * @return {object}
 */
export const kioskTags = (storage = globalThis.localStorage) => {
  let config = null;
  try {
    config = JSON.parse(readStorage(storage, "kiosk_config"));
  } catch {
    config = null;
  }
  return safeTags({
    client_id: config?.client,
    instance_id: config?.instance,
    cart_id: readStorage(storage, "kiosk_cart_id"),
  });
};

const withoutQuery = (url) => typeof url === "string" ? url.split(/[?#]/)[0] : url;

export const scrubBreadcrumb = (breadcrumb) => {
  if (breadcrumb.category === "console") return null;
  return redactForSentry(breadcrumb);
};

/**
 * Deja el evento listo para salir: sin usuario ni datos sensibles, con los tags del tótem.
 * @param {object} event
 * @param {Storage} [storage]
 * @return {object}
 */
export const scrubEvent = (event, storage = globalThis.localStorage) => {
  delete event.user;
  delete event.server_name;
  if (event.request) {
    const userAgent = event.request.headers?.["User-Agent"];
    event.request = { url: withoutQuery(event.request.url), ...(userAgent ? { headers: { "User-Agent": userAgent } } : {}) };
  }
  if (event.message) event.message = redactForSentry(event.message);
  if (event.logentry) event.logentry = redactForSentry(event.logentry);
  for (const exception of event.exception?.values || []) {
    if (exception.value) exception.value = redactForSentry(exception.value);
    for (const frame of exception.stacktrace?.frames || []) delete frame.vars;
  }
  if (event.extra) event.extra = redactForSentry(event.extra);
  if (event.contexts) event.contexts = redactForSentry(event.contexts);
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb).filter(Boolean);
  event.tags = { ...kioskTags(storage), ...event.tags };
  return event;
};

/**
 * Opciones de Sentry.init, o null si Sentry queda apagado.
 * @param {object} env - import.meta.env.
 * @return {object|null}
 */
export const sentryOptions = (env) => {
  const dsn = env.VITE_SENTRY_DSN;
  if (!dsn) return null;
  const inDev = Boolean(env.DEV);
  if (inDev && env.VITE_SENTRY_IN_DEV !== "true") return null;
  const environment = env.REACT_APP_ENV || "unknown";
  return {
    dsn,
    release: RELEASE,
    environment,
    sendDefaultPii: false,
    integrations: [Sentry.breadcrumbsIntegration({ console: false })],
    initialScope: { tags: safeTags({ app: APP, env: environment, dev: inDev ? "true" : null }) },
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: scrubBreadcrumb,
  };
};

export const initSentry = (env = import.meta.env) => {
  const options = sentryOptions(env);
  if (options) Sentry.init(options);
};
